import { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import { Button, Input, Modal, Stepper } from '@/ui/primitives';
import { cn } from '@/lib/utils';
import type { MipCatalogo, MipItem } from '../schemas';
import type { Medicao } from '../offline';

const CATEGORIA_LABEL: Record<string, string> = {
  praga: 'Pragas', doenca: 'Doenças', daninha: 'Daninhas', nutricao: 'Nutrição', outra: 'Outras',
};
const FASE_LABEL: Record<string, string> = { adulto: 'Adultos', ninfa: 'Ninfas', ovo: 'Ovos', larva: 'Larvas' };

type Counts = Record<string, number>; // chave `${itemId}:${fase|'_'}`

export function AmostraForm({
  open, onClose, catalogo, talhaoNumero, onSave,
}: {
  open: boolean;
  onClose: () => void;
  catalogo: MipCatalogo | null;
  talhaoNumero: string | null;
  onSave: (medicoes: Medicao[], observacao: string) => void;
}) {
  const [counts, setCounts] = useState<Counts>({});
  const [observacao, setObservacao] = useState('');
  const [busca, setBusca] = useState('');

  const itens = catalogo?.itens ?? [];
  const grupos = useMemo(() => {
    const q = busca.trim().toLowerCase();
    const filtrados = q
      ? itens.filter((i) => i.nome.toLowerCase().includes(q) || (i.nome_cientifico ?? '').toLowerCase().includes(q))
      : itens;
    const by: Record<string, MipItem[]> = {};
    for (const i of filtrados) (by[i.categoria] ||= []).push(i);
    return by;
  }, [itens, busca]);

  function set(itemId: number, fase: string, v: number) {
    setCounts((c) => ({ ...c, [`${itemId}:${fase}`]: v }));
  }
  function get(itemId: number, fase: string) {
    return counts[`${itemId}:${fase}`] || 0;
  }

  function salvar() {
    const medicoes: Medicao[] = [];
    for (const it of itens) {
      const fases = it.fases.length ? it.fases : ['_'];
      for (const fase of fases) {
        const v = get(it.id, fase);
        if (v > 0) {
          medicoes.push({
            mip_item_id: it.id,
            item_nome: it.nome,
            fase: fase === '_' ? null : fase,
            valor: v,
            unidade: it.unidade,
          });
        }
      }
    }
    onSave(medicoes, observacao);
    setCounts({});
    setObservacao('');
    setBusca('');
  }

  const totalLancado = Object.values(counts).filter((v) => v > 0).length;

  return (
    <Modal open={open} onOpenChange={(v) => !v && onClose()} title={`Amostragem${talhaoNumero ? ` · Talhão ${talhaoNumero}` : ''}`}>
      <div className="flex flex-col gap-3">
        <div className="relative">
          <Search size={14} strokeWidth={1.5} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
          <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar praga/doença/daninha…" className="pl-8" />
        </div>

        <div className="max-h-[50vh] overflow-y-auto flex flex-col gap-3 pr-1">
          {Object.keys(grupos).length === 0 && (
            <p className="text-sm text-text-muted">Nenhum item no catálogo. Configure em Agronomia → Catálogo.</p>
          )}
          {(['praga', 'doenca', 'daninha', 'nutricao', 'outra'] as const).map((cat) => {
            const lista = grupos[cat];
            if (!lista || !lista.length) return null;
            return (
              <div key={cat} className="flex flex-col gap-1.5">
                <div className="text-xs font-semibold uppercase tracking-wider text-text-muted">{CATEGORIA_LABEL[cat]}</div>
                {lista.map((it) => (
                  <div key={it.id} className="rounded-lg border border-border bg-surface px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm text-text truncate">{it.nome}</div>
                        {it.nome_cientifico && <div className="text-[11px] italic text-text-muted truncate">{it.nome_cientifico}</div>}
                      </div>
                      <span className="text-[11px] text-text-muted shrink-0">{it.unidade}</span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-3">
                      {(it.fases.length ? it.fases : ['_']).map((fase) => (
                        <div key={fase} className="flex items-center gap-1.5">
                          {fase !== '_' && <span className="text-[11px] text-text-2 w-12">{FASE_LABEL[fase] ?? fase}</span>}
                          <Stepper value={get(it.id, fase)} onChange={(v) => set(it.id, fase, v)} ariaLabel={`${it.nome}${fase !== '_' ? ' ' + (FASE_LABEL[fase] ?? fase) : ''}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-text-2">Observação do ponto (opcional)</span>
          <textarea
            rows={2}
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
            className={cn(
              'w-full rounded-md bg-elevated border-[1.5px] border-border2 px-3 py-2 text-sm text-text resize-y',
              'focus:outline-none focus:border-[var(--accent)]',
            )}
            placeholder="Ex.: reboleira no pé do talhão…"
          />
        </label>

        <div className="flex items-center justify-between gap-2 border-t border-border pt-3">
          <span className="text-xs text-text-muted">{totalLancado} contagem(ns)</span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              <X size={14} strokeWidth={2} />
              Cancelar
            </Button>
            <Button variant="primary" onClick={salvar}>
              Salvar ponto
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
